import { HttpClient, HttpEvent, HttpEventType, HttpProgressEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface FileUploadProgress {
  progress: number;
  loaded: number;
  total: number;
}

export interface UploadedFile {
  id: number;
  url: string;
  filename: string;
  originalName: string;
  size: number;
  /** MIME type of the stored file, e.g. 'application/pdf'. */
  mimetype: string;
  /** FileType category ('document', 'avatar', ...) - NOT the MIME type. */
  type: string;
  isMain: boolean;
  sortOrder: number;
  description?: string;
  isPrivate?: boolean;
}

// Backend API Response interface
export interface BackendApiResponse<T> {
  success: boolean;
  statusCode: number;
  message: string;
  data: T;
  meta?: Record<string, unknown>;
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private readonly apiUrl = '/api/company/files'; // [OK] Використовуємо company endpoints для approved статусу

  constructor(private http: HttpClient) {}

  uploadAvatar(file: File): Observable<UploadedFile> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<BackendApiResponse<UploadedFile>>(`${this.apiUrl}/avatar`, formData)
      .pipe(map(response => response.data));
  }

  uploadUserPhoto(file: File, userId: number, description?: string): Observable<UploadedFile> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    return this.http.post<BackendApiResponse<UploadedFile>>(`${this.apiUrl}/user/${userId}/photos`, formData)
      .pipe(map(response => response.data));
  }

  uploadUserDocument(file: File, userId: number, description?: string): Observable<UploadedFile> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    return this.http.post<BackendApiResponse<UploadedFile>>(`${this.apiUrl}/user/${userId}/documents`, formData)
      .pipe(map(response => response.data));
  }

  uploadWithProgress(file: File, userId: number, description?: string): Observable<FileUploadProgress | UploadedFile> {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('description', description);
    }

    return this.http.post<BackendApiResponse<UploadedFile>>(`${this.apiUrl}/user/${userId}/photos`, formData, {
      reportProgress: true,
      observe: 'events'
    }).pipe(
      map((event: HttpEvent<BackendApiResponse<UploadedFile>>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress: {
            const progress = event as HttpProgressEvent;
            return {
              progress: Math.round(100 * progress.loaded / (progress.total || 1)),
              loaded: progress.loaded,
              total: progress.total || 0
            } as FileUploadProgress;
          }
          case HttpEventType.Response: {
            return event.body?.data as UploadedFile;
          }
          default: {
            return { progress: 0, loaded: 0, total: 0 } as FileUploadProgress;
          }
        }
      }),
      filter(result => result !== null)
    );
  }

  getUserPhotos(userId: number): Observable<UploadedFile[]> {
    return this.http.get<BackendApiResponse<UploadedFile[]>>(`${this.apiUrl}/user/${userId}/photos`)
      .pipe(map(response => response.data));
  }

  getUserDocuments(userId: number): Observable<UploadedFile[]> {
    return this.http.get<BackendApiResponse<UploadedFile[]>>(`${this.apiUrl}/user/${userId}/documents`)
      .pipe(map(response => response.data));
  }

  getUserAvatar(userId: number): Observable<UploadedFile | null> {
    return this.http.get<BackendApiResponse<UploadedFile | null>>(`${this.apiUrl}/user/${userId}/avatar`)
      .pipe(map(response => response.data));
  }

  deleteFile(fileId: number): Observable<{ message: string }> {
    return this.http.delete<BackendApiResponse<{ message: string }>>(`${this.apiUrl}/${fileId}`)
      .pipe(map(response => response.data));
  }

  setMainPhoto(fileId: number, ownerId: number, ownerType: string, type: string): Observable<UploadedFile> {
    return this.http.put<BackendApiResponse<UploadedFile>>(`${this.apiUrl}/${fileId}/set-main`, {
      ownerId,
      ownerType,
      type
    }).pipe(map(response => response.data));
  }

  reorderPhotos(photoIds: number[]): Observable<{ message: string }> {
    return this.http.put<BackendApiResponse<{ message: string }>>(`${this.apiUrl}/reorder`, { photoIds })
      .pipe(map(response => response.data));
  }

  createAvatarFromPhoto(photoId: number, userId: number): Observable<UploadedFile> {
    return this.http.post<BackendApiResponse<UploadedFile>>(`${this.apiUrl}/${photoId}/create-avatar`, {
      userId
    }).pipe(map(response => response.data));
  }

  getFileById(fileId: number): Observable<UploadedFile> {
    return this.http.get<BackendApiResponse<UploadedFile>>(`${this.apiUrl}/${fileId}`)
      .pipe(map(response => response.data));
  }

  getSignedUrl(fileId: number): Observable<string> {
    return this.http
      .get<BackendApiResponse<{ url: string }>>(`${this.apiUrl}/${fileId}/signed-url`)
      .pipe(map(response => response.data.url));
  }

  /**
   * Raw bytes streamed from our own API. Needed to render DOCX/XLSX in the
   * browser: reading a storage URL directly would require CORS on the bucket.
   */
  getFileContent(fileId: number): Observable<ArrayBuffer> {
    return this.http.get(`${this.apiUrl}/${fileId}/content`, {
      responseType: 'arraybuffer'
    });
  }
}
